import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { CPAPFilterType } from 'components/SearchCpapPage';
import { cpapInstructions } from 'containers/App/selectors';
import { useScrollTop } from 'hooks';
import moment from 'moment';
import React, { useState, FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { HeaderTitle, SearchCpapPage, Pagination, Table } from 'components';
import { t_cpap_instructions } from 'types';
import { RootState } from '../../store';
import { getDataStoreFields } from '../Register/RegisterFirstStep/thunk';
import { setCurrentPage, resetSearchValue, setSearchValue, setItems, setTotalItems } from './slices';
import Wrap from './styles';
import { getCpapInstructItems, getCpapInstructItemsExport, getListMedicalSearch } from './thunk';

const Cpap: FC = () => {
  const [exporting, setExporting] = useState<boolean>(false);
  const { items, totalItems, currentPage, searchValue, loading } = useSelector(cpapInstructions);
  const { currentApplicationId } = useSelector((state: RootState) => state.workSpaces);

  const history = useHistory();
  const dispatch = useDispatch();

  const changePage = useCallback(
    (pageNumber: number) => {
      dispatch(setCurrentPage(pageNumber));
      checkHealthAuthorities(pageNumber, false);
    },
    [dispatch, searchValue],
  );

  const onSearch = async (values?: CPAPFilterType) => {
    const data: any =
      values?.health_authorities && (await dispatch(getListMedicalSearch({ pageNumber: 1, searchValue: values })));
    if (data && getListMedicalSearch.fulfilled.match(data)) {
      dispatch(
        setSearchValue({
          ...values,
          health_authorities: values?.health_authorities ? values.health_authorities : '',
          health_authorities_iids:
            data.payload?.items.length > 0 ? data.payload?.items.map((item: any) => item.i_id) : [],
        }),
      );
      if (data.payload.items.length == 0) {
        dispatch(setItems([]));
        dispatch(setTotalItems(0));
      } else {
        dispatch(
          getCpapInstructItems({
            pageNumber: 1,
            searchValue: {
              ...values,
              health_authorities_iids:
                data.payload?.items.length > 0 ? data.payload?.items.map((item: any) => item.i_id) : [],
            },
          }),
        );
      }
    } else {
      if (values) {
        dispatch(
          setSearchValue({
            ...values,
            health_authorities: '',
            health_authorities_iids: [],
          }),
        );
      } else {
        dispatch(resetSearchValue());
      }
      dispatch(setCurrentPage(1));
      dispatch(
        getCpapInstructItems({
          pageNumber: 1,
          searchValue: {
            ...values,
            health_authorities: '',
            health_authorities_iids: [],
          },
        }),
      );
    }
  };

  const detailItemSelected = (itemId: string) => {
    history.push('/cpap/detail', { itemId: itemId });
  };

  const checkHealthAuthorities = async (pageNumber: number, isCSV: boolean) => {
    if (searchValue.health_authorities) {
      const data: any = await dispatch(getListMedicalSearch({ pageNumber: 1, searchValue: searchValue }));
      if (data && getListMedicalSearch.fulfilled.match(data)) {
        const medicalData = data.payload?.items.length > 0 ? data.payload?.items.map((item: any) => item.i_id) : [];
        if (isCSV) {
          generateCSV(medicalData);
        } else {
          dispatch(
            getCpapInstructItems({
              pageNumber: pageNumber,
              searchValue: {
                ...searchValue,
                health_authorities_iids:
                  data.payload?.items.length > 0 ? data.payload?.items.map((item: any) => item.i_id) : [],
              },
            }),
          );
        }
      }
    } else {
      if (isCSV) {
        generateCSV([]);
      } else {
        dispatch(getCpapInstructItems({ pageNumber: pageNumber, searchValue: searchValue }));
      }
    }
  };
  const generateCSV = async (medicalData: string[]) => {
    setExporting(true);
    const resultAction: any = await dispatch(
      getCpapInstructItemsExport({ searchValue: { ...searchValue, health_authorities_iids: medicalData } }),
    );
    setExporting(false);
    if (getCpapInstructItemsExport.fulfilled.match(resultAction)) {
      const listCsv =
        resultAction.payload.items.length > 0
          ? resultAction.payload.items.map((item: t_cpap_instructions) => ({
              facility_name: item.facility_name,
              clerk: item.clerk,
              transfer_history: `"${item.transfer_history || ''}"`,
              sp_price: item.sp_price,
              patient_name: item.patient_name,
              patient_name_kana: item.patient_name_kana,
              gender: item.gender,
              patient_birthday: item.patient_birthday ? moment(item.patient_birthday).format('YYYY/MM/DD') : '',
              zip_code: item.zip_code,
              address_1: item.address_1,
              address_2: item.address_2,
              phone_number: item.phone_number,
              mobile_phone_number: item.mobile_phone_number,
              patient_id: item.patient_id,
              pressure_min: item.pressure_min,
              pressure_max: item.pressure_max,
              lamp_time: item.lamp_time,
              lamp_time_max: item.lamp_time_max,
              machine: item.machine,
              machine_other: item.machine_other,
              machine_no: item.machine_no,
              machine_epr_no: item.machine_epr_no,
              machine_dn_no: item.machine_dn_no,
              acc_mask: item.acc_mask,
              acc_mask_other: item.acc_mask_other,
              acc_mask_size: item.acc_mask_size,
              acc_mask_size_other: item.acc_mask_size_other,
              accessories: JSON.stringify(item.accessories || ''),
              humidifier_machine_no: item.humidifier_machine_no,
              last_month: item.last_month,
              installation_date: item.installation_date ? moment(item.installation_date).format('YYYY/MM/DD') : '',
              cpap_remark: `"${item.cpap_remark || ''}"`,
              remark_for_output: `"${item.remark_for_output || ''}"`,
              suspension_flg: item.suspension_flg,
              sort_num: item.sort_num,
            }))
          : [];
      const csvString = [
        [
          '医療機関名',
          '事務担当',
          '転院履歴',
          '特別料金対象者',
          '患者名',
          '患者名（ふりがな）',
          '性別',
          '生年月日',
          '郵便番号',
          '住所１',
          '住所２',
          '電話番号',
          '携帯番号',
          '患者ID',
          '圧力下限値',
          '圧力上限値',
          'ランプ時間',
          '最大ランプ時間',
          '機種',
          '機種その他',
          '機番',
          'EPR',
          'DN/キー',
          'マスク種類',
          'マスク種類その他',
          'マスクサイズ',
          'マスクサイズその他',
          '付属品',
          '加湿器機番',
          '次回定期送付月',
          '設置日',
          '備考',
          '受診明細出力用備考',
          '利用停止フラグ',
          '表示順',
        ],
        ...listCsv.map((item: any) => Object.values(item)),
      ]
        .map((e) => e.join(','))
        .join('\n');

      const bom = '\uFEFF';
      const file = new Blob([bom, csvString], { type: 'application/octet-stream' });
      saveAs(file, 'CPAP一覧.csv');
    }
  };

  useScrollTop();

  useEffect(() => {
    currentApplicationId &&
      dispatch(getDataStoreFields({ appId: currentApplicationId, dataStoreId: 't_cpap_instructions' }));
  }, [dispatch, currentApplicationId]);

  useEffect(() => {
    checkHealthAuthorities(currentPage, false);
    return () => {
      if (history.location.pathname !== '/cpap/detail') {
        dispatch(resetSearchValue());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history.location.pathname]);

  return (
    <Wrap>
      <HeaderTitle title="CPAP指示書一覧" icon={SearchOutlined}>
        <SearchCpapPage onSearch={onSearch} />
      </HeaderTitle>
      <div className="export-csv-cpap">
        <span className="">検索した条件で</span>
        <Button onClick={() => checkHealthAuthorities(1, true)} className="btn-output" loading={exporting}>
          <DownloadOutlined /> CSV出力
        </Button>
      </div>
      <div className="section">
        <Pagination totalItems={totalItems} page={currentPage} onChangePage={changePage} />
        <div className="table">
          <Table updateData={items} moveTo={detailItemSelected} loading={loading} />
        </div>
      </div>
    </Wrap>
  );
};

export default Cpap;
